import type { ReactNode } from "react";
import type { BlockingReason, BookingOption, GuaranteeLevel, Money } from "../bridge/contract";

/**
 * The small shared vocabulary of the flow. Kept in one file the way the guest app does it —
 * a component library for eleven screens costs more than it returns.
 */

export function Panel({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="panel">
      <header className="panel-head">
        <h2>{title}</h2>
        {description ? <p className="muted">{description}</p> : null}
      </header>
      {children}
    </section>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  reason,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  reason?: BlockingReason;
  children: ReactNode;
}) {
  const errorId = reason ? `${htmlFor}-error` : undefined;
  return (
    <div className={`field${reason ? " field--invalid" : ""}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && !reason ? <p className="field-hint">{hint}</p> : null}
      {reason ? (
        <p className="field-error" id={errorId} role="alert">
          {reason.message}
        </p>
      ) : null}
    </div>
  );
}

/**
 * States plainly what the hotel is promising.
 *
 * The whole point of the option model is that a guest can tell a guaranteed sea view from a
 * hoped-for one before they pay, so this badge is never optional and never abbreviated away.
 */
export function GuaranteeBadge({ level }: { level: GuaranteeLevel }) {
  const copy: Record<GuaranteeLevel, { label: string; title: string }> = {
    guaranteed: { label: "Garantiert", title: "Fest zugesagt und Teil der Buchung." },
    requested: { label: "Auf Wunsch", title: "Wir merken es vor, können es aber nicht zusagen." },
    requires_review: { label: "Wird geprüft", title: "Das Hotel prüft das vor der Bestätigung." },
  };
  const { label, title } = copy[level];
  return (
    <span className={`badge badge--${level}`} title={title}>
      {label}
    </span>
  );
}

export function PriceTag({ price, unit }: { price: Money | null; unit?: string | null }) {
  if (!price) return null;
  return (
    <span className="price-tag">
      {price.formatted}
      {unit ? <span className="price-unit"> {unit}</span> : null}
    </span>
  );
}

const UNIT_LABELS: Record<string, string> = {
  per_stay: "pro Aufenthalt",
  per_night: "pro Nacht",
  per_person: "pro Person",
  per_person_per_night: "pro Person / Nacht",
  per_room: "pro Zimmer",
  per_room_per_night: "pro Zimmer / Nacht",
  per_item: "pro Stück",
};

export function OptionRow({
  option,
  checked,
  disabled,
  onToggle,
}: {
  option: BookingOption;
  checked: boolean;
  disabled?: boolean;
  onToggle: (next: boolean) => void;
}) {
  const unavailable = option.availabilityStatus === "unavailable";
  const inputId = `option-${option.code}`;
  return (
    <label className={`option-row${unavailable ? " option-row--unavailable" : ""}`} htmlFor={inputId}>
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled || unavailable}
        onChange={(event) => onToggle(event.target.checked)}
      />
      <span className="option-body">
        <span className="option-title">
          {option.displayName}
          <GuaranteeBadge level={option.guaranteeLevel} />
          {option.availabilityStatus === "limited" ? <span className="badge badge--limited">Begrenzt</span> : null}
        </span>
        {option.description ? <span className="option-description">{option.description}</span> : null}
      </span>
      <PriceTag price={option.price} unit={option.pricingUnit ? UNIT_LABELS[option.pricingUnit] : null} />
    </label>
  );
}

export function ChoiceCard({
  title,
  description,
  meta,
  media,
  selected,
  disabled,
  onSelect,
  children,
}: {
  title: string;
  description?: string | null;
  meta?: ReactNode;
  media?: string | null;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={`choice-card${selected ? " choice-card--selected" : ""}`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
    >
      {media ? (
        // Decorative: the name and the details below carry the meaning, so a screen reader
        // reading out a filename here would only add noise.
        <img className="choice-media" src={media} alt="" loading="lazy" />
      ) : null}
      <span className="choice-body">
        <span className="choice-head">
          <span className="choice-title">{title}</span>
          {meta}
        </span>
        {description ? <span className="choice-description">{description}</span> : null}
        {children}
      </span>
    </button>
  );
}

export function Stepper({ value, min, max, label, onChange }: {
  value: number;
  min: number;
  max: number;
  label: string;
  onChange: (next: number) => void;
}) {
  return (
    <span className="stepper" role="group" aria-label={label}>
      <button type="button" onClick={() => onChange(value - 1)} disabled={value <= min} aria-label={`${label} verringern`}>
        −
      </button>
      <output aria-live="polite">{value}</output>
      <button type="button" onClick={() => onChange(value + 1)} disabled={value >= max} aria-label={`${label} erhöhen`}>
        +
      </button>
    </span>
  );
}

export function Notice({ tone, title, children }: { tone: "info" | "warning" | "error"; title: string; children?: ReactNode }) {
  return (
    <div className={`notice notice--${tone}`} role={tone === "error" ? "alert" : "status"}>
      <strong>{title}</strong>
      {children}
    </div>
  );
}
